import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import CameraFileInput from '../components/CameraFileInput';
import { SHOW_INSTITUTO_BW_BRANDING } from '../lib/branding';
import { hasPermission } from '../lib/permissions';
import { appTodayDate, toAppDateInputValue, toAppTimeInputValue } from '../lib/appDateTime';
import { 
  Clock, 
  Pill, 
  CheckCircle2, 
  AlertCircle, 
  AlertTriangle, 
  Circle,
  Settings,
  RefreshCw,
  MapPin,
  ExternalLink,
  Syringe,
  Filter,
  Calendar
} from 'lucide-react';

const canSetProtocolExecutionDateTime = (user) => ['veterinario', 'administrador', 'programador'].includes(user?.role);
const PROTOCOL_CONFIRMATION_KEYWORD = 'CONFIRMAR';

const toDateInputValue = (dateValue = new Date()) => {
  return toAppDateInputValue(dateValue);
};

const toTimeInputValue = (dateValue = new Date()) => {
  return toAppTimeInputValue(dateValue);
};

const medicationNeedsRetroactiveExecutionConfirmation = (medication, executionDateValue) => {
  if (!medication) return false;
  if (medication.status === 'cancelled' || medication.cancelled_at) return true;
  if (medication.status === 'suspended' || medication.suspended_at || medication.active === false) return true;
  const executionDay = executionDateValue || toDateInputValue();
  const endDay = medication.end_date ? toDateInputValue(medication.end_date) : '';
  return Boolean(endDay && executionDay > endDay);
};

const getMedicationProtocolStatusLabel = (medication) => {
  if (medication?.status === 'cancelled' || medication?.cancelled_at) return 'Cancelado';
  if (medication?.status === 'suspended' || medication?.suspended_at || medication?.active === false) return 'Suspenso';
  return '';
};

export default function MedicationDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [groupedSchedules, setGroupedSchedules] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);
  const [summary, setSummary] = useState(null);
  const [bases, setBases] = useState([]);
  const [settings, setSettings] = useState({ alert_window_minutes: 30 });
  const [selectedDateMeta, setSelectedDateMeta] = useState({ isToday: true, isPastDate: false, isFutureDate: false });
  const defaultBaseFilter = Array.isArray(user?.base_ids) && user.base_ids.length === 1 ? user.base_ids[0] : 'all';
  const canExecuteMedication = hasPermission(user, 'medication.execute');
  
  // Filters
  const [filterBase, setFilterBase] = useState(defaultBaseFilter);
  const [filterShift, setFilterShift] = useState('all');
  const [filterDate, setFilterDate] = useState(() => appTodayDate());
  
  // Settings dialog
  const [showSettings, setShowSettings] = useState(false);
  const [alertWindow, setAlertWindow] = useState(30);
  
  // Execution dialog
  const [showExecuteDialog, setShowExecuteDialog] = useState(false);
  const [executingItem, setExecutingItem] = useState(null);
  const [executeNotes, setExecuteNotes] = useState('');
  const [executeStatus, setExecuteStatus] = useState('realizado');
  const [executeDate, setExecuteDate] = useState('');
  const [executeTime, setExecuteTime] = useState('');
  const [executeDateTimeJustification, setExecuteDateTimeJustification] = useState('');
  const [executeMediaFile, setExecuteMediaFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Current time for live updates
  const [currentTime, setCurrentTime] = useState(new Date());
  const canConfigureMedication = canExecuteMedication;

  // Update current time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  // Fetch bases on mount
  useEffect(() => {
    fetchBases();
  }, []);

  useEffect(() => {
    if (defaultBaseFilter !== 'all' && filterBase === 'all') {
      setFilterBase(defaultBaseFilter);
    }
  }, [defaultBaseFilter, filterBase]);

  // Fetch dashboard data when filters change
  useEffect(() => {
    fetchDashboardData();
  }, [filterBase, filterShift, filterDate]);

  // Auto-refresh every 5 minutes
  useEffect(() => {
    const refreshTimer = setInterval(() => {
      fetchDashboardData(true);
    }, 300000);
    return () => clearInterval(refreshTimer);
  }, [filterBase, filterShift, filterDate]);

  const fetchBases = async () => {
    try {
      const response = await axios.get(`${API}/bases`);
      setBases(response.data);
    } catch (error) {
      console.error('Error fetching bases:', error);
    }
  };

  const fetchDashboardData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    
    try {
      const params = new URLSearchParams();
      if (filterBase && filterBase !== 'all') params.append('base_id', filterBase);
      if (filterShift && filterShift !== 'all') params.append('shift', filterShift);
      if (filterDate) params.append('date', filterDate);
      
      const response = await axios.get(`${API}/medication-dashboard?${params.toString()}`);
      setGroupedSchedules(response.data.grouped_schedules || []);
      setTimeSlots(response.data.time_slots || []);
      setSummary(response.data.summary || null);
      setSettings(response.data.settings || { alert_window_minutes: 30 });
      setAlertWindow(response.data.settings?.alert_window_minutes || 30);
      setSelectedDateMeta({
        isToday: response.data.is_today,
        isPastDate: response.data.is_past_date,
        isFutureDate: response.data.is_future_date
      });
    } catch (error) {
      toast.error('Erro ao carregar dashboard de medicação');
      console.error('Error:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [filterBase, filterShift, filterDate]);

  const handleExecuteClick = (animal, medication, hour, slotStatus = 'pending') => {
    const now = new Date();
    setExecutingItem({ 
      ...animal, 
      medication,
      scheduled_hour: hour,
      slot_status: slotStatus
    });
    setExecuteNotes('');
    setExecuteStatus('realizado');
    setExecuteDate(toDateInputValue(now));
    setExecuteTime(toTimeInputValue(now));
    setExecuteDateTimeJustification('');
    setExecuteMediaFile(null);
    setShowExecuteDialog(true);
  };

  const handleExecuteSubmit = async () => {
    if (!executingItem) return;
    
    setSubmitting(true);
    try {
      const deviceType = /Mobi|Android/i.test(navigator.userAgent) ? 'mobile' : 'desktop';
      const protocolNeedsConfirmation = medicationNeedsRetroactiveExecutionConfirmation(executingItem.medication, executeDate);
      let protocolConfirmation = null;
      if (protocolNeedsConfirmation) {
        const typed = window.prompt(`Este protocolo está encerrado, suspenso ou cancelado. Para registrar a execução retroativa, digite ${PROTOCOL_CONFIRMATION_KEYWORD}.`);
        if (typed === null) {
          setSubmitting(false);
          return;
        }
        if (typed.trim().toUpperCase() !== PROTOCOL_CONFIRMATION_KEYWORD) {
          toast.error(`Confirmação inválida. Digite ${PROTOCOL_CONFIRMATION_KEYWORD} para continuar.`);
          setSubmitting(false);
          return;
        }
        protocolConfirmation = PROTOCOL_CONFIRMATION_KEYWORD;
      }
      
      const response = await axios.post(
        `${API}/medication-dashboard/execute/${executingItem.medication.protocol_id}?scheduled_hour=${executingItem.scheduled_hour}&date=${filterDate}`,
        {
          status: executeStatus,
          notes: executeNotes || null,
          device_type: deviceType,
          ...(protocolConfirmation ? { outcome_override_confirmation: protocolConfirmation } : {}),
          ...(canSetProtocolExecutionDateTime(user) ? {
            execution_date: executeDate || null,
            execution_time: executeTime || null,
            date_time_justification: executeDateTimeJustification || null
          } : {})
        }
      );

      let uploadedMedia = false;
      let mediaUploadFailed = false;
      const executionId = response.data?.execution?.id;
      if (executeMediaFile && executionId) {
        const formData = new FormData();
        formData.append('file', executeMediaFile);
        formData.append('caption', executeMediaFile.name);
        try {
          await axios.post(`${API}/protocol-executions/${executionId}/media`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          uploadedMedia = true;
        } catch (uploadError) {
          mediaUploadFailed = true;
          console.error('Error uploading protocol execution media:', uploadError);
        }
      }
      
      if (mediaUploadFailed) {
        toast.error('Medicação registrada, mas a foto não foi enviada');
      } else {
        toast.success(uploadedMedia ? 'Medicação registrada com foto!' : 'Medicação registrada com sucesso!');
      }
      setShowExecuteDialog(false);
      setExecuteMediaFile(null);
      fetchDashboardData(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao registrar medicação');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateSettings = async () => {
    try {
      await axios.put(`${API}/medication-dashboard/settings?alert_window_minutes=${alertWindow}`);
      toast.success('Configurações salvas!');
      setShowSettings(false);
      fetchDashboardData(true);
    } catch (error) {
      toast.error('Erro ao salvar configurações');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'done': return 'bg-blue-100 text-blue-700 border-blue-300';
      case 'upcoming': return 'bg-yellow-100 text-yellow-700 border-yellow-300';
      case 'overdue': return 'bg-red-100 text-red-700 border-red-300';
      default: return 'bg-gray-100 text-gray-600 border-gray-300';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'done': return <CheckCircle2 className="w-4 h-4" />;
      case 'upcoming': return <AlertTriangle className="w-4 h-4" />;
      case 'overdue': return <AlertCircle className="w-4 h-4" />;
      default: return <Circle className="w-4 h-4" />;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'done': return 'Aplicado';
      case 'upcoming': return 'Próximo';
      case 'overdue': return 'Atrasado';
      default: return 'Pendente';
    }
  };

  const formatHour = (hour) => {
    return `${hour.toString().padStart(2, '0')}:00`;
  };

  const getGroupBadgeColor = (group) => {
    switch (group) {
      case 'AVE': return 'bg-sky-500 text-white';
      case 'MAM': return 'bg-amber-500 text-white';
      case 'REPT': return 'bg-green-600 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  const getRouteBadgeColor = (route) => {
    switch (route?.toUpperCase()) {
      case 'IV': return 'bg-red-100 text-red-700';
      case 'IM': return 'bg-orange-100 text-orange-700';
      case 'SC': return 'bg-purple-100 text-purple-700';
      case 'ORAL': return 'bg-green-100 text-green-700';
      case 'TÓPICA': return 'bg-blue-100 text-blue-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const isTherapyProtocol = (medication) => medication?.protocol_type === 'therapy';

  // Check if current time is at or past this hour slot
  const isCurrentOrPastHour = (hour) => {
    if (!selectedDateMeta.isToday) {
      return selectedDateMeta.isPastDate;
    }
    const now = currentTime.getHours();
    const minutes = currentTime.getMinutes();
    // Current hour or past
    return now > hour || (now === hour && minutes >= 0);
  };

  // Check if this is the current hour slot (for highlighting)
  const isCurrentHourSlot = (hour) => {
    if (!selectedDateMeta.isToday) return false;
    const now = currentTime.getHours();
    return now === hour;
  };

  // Find the position of current time indicator between slots
  const getCurrentTimePosition = () => {
    const now = currentTime.getHours();
    const minutes = currentTime.getMinutes();
    
    // Find which slot we're at or after
    for (let i = 0; i < timeSlots.length; i++) {
      if (timeSlots[i] === now) {
        // We're at this slot
        return { slotIndex: i, isAtSlot: true, minutesPast: minutes };
      }
      if (i < timeSlots.length - 1 && now > timeSlots[i] && now < timeSlots[i + 1]) {
        // We're between slots
        return { slotIndex: i, isAtSlot: false, hoursPast: now - timeSlots[i] };
      }
    }
    // Check if we're past the last slot
    if (now >= timeSlots[timeSlots.length - 1]) {
      return { slotIndex: timeSlots.length - 1, isAtSlot: now === timeSlots[timeSlots.length - 1], minutesPast: minutes };
    }
    // Before first slot
    return { slotIndex: -1, isAtSlot: false };
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 via-teal-50 to-green-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
          <p className="mt-4 text-emerald-700">Carregando dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen overflow-x-hidden bg-gradient-to-br from-emerald-50 via-teal-50 to-green-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-emerald-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="block md:hidden space-y-3">
            <div className="flex min-w-0 items-start space-x-3">
              {SHOW_INSTITUTO_BW_BRANDING && (
                <img
                  src="/bw-symbol-final.png"
                  alt="Instituto BW"
                  className="h-11 w-11 flex-shrink-0 object-contain"
                />
              )}
              <div className={`min-w-0 ${SHOW_INSTITUTO_BW_BRANDING ? 'border-l border-emerald-200 pl-3' : ''}`}>
                <h1 className="break-words text-lg font-bold text-emerald-800" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Dashboard de Medicação
                </h1>
                <p className="text-xs text-emerald-500">Rotina de Medicações</p>
              </div>
            </div>
            <div className="space-y-2">
              <span className="flex items-center text-sm text-gray-600">
                <Clock className="w-4 h-4 mr-1" />
                {currentTime.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
              </span>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <Button
                  onClick={() => fetchDashboardData(true)}
                  variant="outline"
                  size="sm"
                  disabled={refreshing}
                  className="w-full border-emerald-600 text-emerald-600"
                  data-testid="refresh-btn"
                >
                  <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                </Button>
                {canConfigureMedication && (
                  <Button
                    onClick={() => setShowSettings(true)}
                    variant="outline"
                    size="sm"
                    className="w-full border-gray-400 text-gray-600"
                    data-testid="settings-btn"
                  >
                    <Settings className="w-4 h-4" />
                  </Button>
                )}
                <Button
                  onClick={() => navigate('/')}
                  variant="outline"
                  size="sm"
                  className="w-full border-emerald-600 text-emerald-600"
                  data-testid="back-dashboard-btn"
                >
                  Voltar
                </Button>
                <Button
                  onClick={logout}
                  variant="ghost"
                  size="sm"
                  className="w-full text-red-600 hover:bg-red-50"
                >
                  Sair
                </Button>
              </div>
            </div>
          </div>
          <div className="hidden md:flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {SHOW_INSTITUTO_BW_BRANDING && (
                <img
                  src="/bw-symbol-final.png"
                  alt="Instituto BW"
                  className="h-12 w-12 object-contain"
                />
              )}
              <div className={SHOW_INSTITUTO_BW_BRANDING ? 'border-l border-emerald-200 pl-3' : ''}>
                <h1 className="text-lg md:text-xl font-bold text-emerald-800" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Dashboard de Medicação
                </h1>
                <p className="text-xs text-emerald-500">Rotina de Medicações</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <span className="flex items-center text-sm text-gray-600">
                <Clock className="w-4 h-4 mr-1" />
                {currentTime.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
              </span>
              <Button
                onClick={() => fetchDashboardData(true)}
                variant="outline"
                size="sm"
                disabled={refreshing}
                className="border-emerald-600 text-emerald-600"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              </Button>
              {canConfigureMedication && (
                <Button
                  onClick={() => setShowSettings(true)}
                  variant="outline"
                  size="sm"
                  className="border-gray-400 text-gray-600"
                >
                  <Settings className="w-4 h-4" />
                </Button>
              )}
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                size="sm"
                className="border-emerald-600 text-emerald-600"
              >
                Voltar
              </Button>
              <Button
                onClick={logout}
                variant="ghost"
                size="sm"
                className="text-red-600 hover:bg-red-50"
              >
                Sair
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
            <Card className="bg-white border-emerald-200">
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-emerald-700">{summary.total_animals}</div>
                <div className="text-xs text-gray-600">Animais</div>
              </CardContent>
            </Card>
            <Card className="bg-white border-emerald-200">
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-emerald-700">{summary.total_medications}</div>
                <div className="text-xs text-gray-600">Medicações</div>
              </CardContent>
            </Card>
            <Card className="bg-gray-50 border-gray-200">
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-gray-600">{summary.pending}</div>
                <div className="text-xs text-gray-600">Pendentes</div>
              </CardContent>
            </Card>
            <Card className="bg-yellow-50 border-yellow-200">
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-yellow-600">{summary.upcoming}</div>
                <div className="text-xs text-gray-600">Próximos</div>
              </CardContent>
            </Card>
            <Card className="bg-red-50 border-red-200">
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-red-600">{summary.overdue}</div>
                <div className="text-xs text-gray-600">Atrasados</div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card className="mb-6 border-emerald-100">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4 items-end">
              <div className="w-full md:flex-1">
                <Label className="flex items-center gap-1 mb-2">
                  <Calendar className="w-4 h-4" /> Data
                </Label>
                <Input
                  type="date"
                  value={filterDate}
                  onChange={(e) => setFilterDate(e.target.value)}
                  className="min-h-[44px]"
                  data-testid="filter-date-input"
                />
              </div>
              <div className="flex-1">
                <Label className="flex items-center gap-1 mb-2">
                  <Filter className="w-4 h-4" /> Base
                </Label>
                <Select value={filterBase} onValueChange={setFilterBase}>
                  <SelectTrigger data-testid="filter-base-select" className="min-h-[44px]">
                    <SelectValue placeholder="Todas as bases" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas as bases</SelectItem>
                    {bases.map((base) => (
                      <SelectItem key={base.id} value={base.id}>{base.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1">
                <Label className="flex items-center gap-1 mb-2">
                  <Clock className="w-4 h-4" /> Turno
                </Label>
                <Select value={filterShift} onValueChange={setFilterShift}>
                  <SelectTrigger data-testid="filter-shift-select" className="min-h-[44px]">
                    <SelectValue placeholder="Todos os turnos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os turnos</SelectItem>
                    <SelectItem value="morning">Manhã (6h - 12h)</SelectItem>
                    <SelectItem value="afternoon">Tarde (12h - 18h)</SelectItem>
                    <SelectItem value="night">Noite (18h - 6h)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Legend */}
        <div className="flex flex-wrap gap-3 mb-4 text-sm">
          <div className="flex items-center gap-1">
            <span className="w-4 h-4 rounded bg-gray-200"></span>
            <span>Pendente</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="w-4 h-4 rounded bg-yellow-300"></span>
            <span>Próximo ({settings.alert_window_minutes}min)</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="w-4 h-4 rounded bg-red-400"></span>
            <span>Atrasado</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="w-4 h-4 rounded bg-blue-400"></span>
            <span>Aplicado</span>
          </div>
        </div>
        {/* No data message */}
        {groupedSchedules.length === 0 ? (
          <Card className="border-emerald-100">
            <CardContent className="py-12 text-center">
              <Pill className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">Nenhum animal com medicação programada</p>
              <p className="text-sm text-gray-400 mt-2">Cadastre protocolos de medicação nos prontuários</p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Desktop: Grouped Table View */}
            <div className="hidden lg:block space-y-4">
              {groupedSchedules.map((animal) => (
                <Card key={animal.animal_id} className="border-emerald-100 overflow-hidden" data-testid={`animal-card-${animal.animal_id}`}>
                  {/* Animal Header */}
                  <div className="bg-emerald-50 p-4 border-b border-emerald-100">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div>
                          <Link 
                            to={`/animals/${animal.animal_id}/record`}
                            className="text-lg font-bold text-emerald-800 hover:text-emerald-900 hover:underline flex items-center gap-1"
                            data-testid={`animal-link-${animal.animal_id}`}
                          >
                            {animal.ibama_id}
                            <ExternalLink className="w-4 h-4" />
                          </Link>
                          {animal.pmp_id && <p className="text-xs text-sky-700">PMP: {animal.pmp_id}</p>}
                          <p className="text-sm text-gray-600">{animal.common_name} - {animal.species}</p>
                        </div>
                        <Badge className={getGroupBadgeColor(animal.animal_group)}>
                          {animal.animal_group}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <MapPin className="w-4 h-4" />
                        <span>{animal.location} - {animal.bay}</span>
                        <span className="text-gray-400">|</span>
                        <span>{animal.base_name}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Medications Table */}
                  <div className="overflow-x-auto relative">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="text-left p-3 font-semibold text-gray-700 min-w-[250px]">Medicação</th>
                          {timeSlots.map((hour, idx) => {
                            const isCurrent = isCurrentHourSlot(hour);
                            const isPast = isCurrentOrPastHour(hour) && !isCurrent;
                            return (
                              <th 
                                key={hour} 
                                className={`text-center p-3 font-semibold min-w-[90px] relative
                                  ${isCurrent 
                                    ? 'bg-orange-100 text-orange-800 border-x-2 border-orange-400' 
                                    : isPast 
                                      ? 'bg-gray-100 text-gray-500' 
                                      : 'text-gray-700'
                                  }
                                `}
                              >
                                {isCurrent && (
                                  <div className="absolute -top-1 left-1/2 transform -translate-x-1/2">
                                    <div className="bg-orange-500 text-white text-xs px-2 py-0.5 rounded-full font-bold animate-pulse">
                                      AGORA
                                    </div>
                                  </div>
                                )}
                                <div className={isCurrent ? 'mt-2' : ''}>
                                  {formatHour(hour)}
                                </div>
                                {isCurrent && (
                                  <div className="text-xs text-orange-600 font-normal">
                                    {currentTime.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                                  </div>
                                )}
                              </th>
                            );
                          })}
                        </tr>
                      </thead>
                      <tbody>
                        {animal.medications.map((med, idx) => (
                          <tr key={med.protocol_id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            <td className="p-3">
                              <div className="flex items-center gap-2">
                                <Pill className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                                <div>
                                  <div className="flex flex-wrap items-center gap-2">
                                    <span className="font-medium text-emerald-800">{isTherapyProtocol(med) ? (med.therapy_type || med.medication_name) : med.medication_name}</span>
                                    {isTherapyProtocol(med) && <Badge className="bg-blue-100 text-blue-800">Terapia</Badge>}
                                    {getMedicationProtocolStatusLabel(med) && <Badge className="bg-amber-100 text-amber-800">{getMedicationProtocolStatusLabel(med)}</Badge>}
                                  </div>
                                  <div className="flex items-center gap-2 text-xs text-gray-600">
                                    {!isTherapyProtocol(med) && med.dosage && <span>{med.dosage}</span>}
                                    {!isTherapyProtocol(med) && med.route && (
                                      <Badge className={getRouteBadgeColor(med.route)} variant="outline">
                                        {med.route}
                                      </Badge>
                                    )}
                                    {isTherapyProtocol(med) && med.therapy_duration && <span>{med.therapy_duration}</span>}
                                    <span className="text-gray-400">{med.frequency}</span>
                                    {med.start_time && (
                                      <span className="text-emerald-600">início {med.start_time}</span>
                                    )}
                                  </div>
                                  {med.days_remaining !== null && (
                                    <div className="flex items-center gap-1 text-xs mt-1">
                                      <Calendar className="w-3 h-3" />
                                      <span className={med.days_remaining <= 2 ? 'text-orange-600 font-medium' : 'text-gray-500'}>
                                        {med.days_remaining} dia{med.days_remaining !== 1 ? 's' : ''} restante{med.days_remaining !== 1 ? 's' : ''}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                            {timeSlots.map(hour => {
                              const status = med.hour_statuses?.[hour];
                              const isScheduled = med.scheduled_hours?.includes(hour);
                              const isCurrent = isCurrentHourSlot(hour);
                              const isPast = isCurrentOrPastHour(hour) && !isCurrent;
                              
                              if (!isScheduled) {
                                return (
                                  <td 
                                    key={hour} 
                                    className={`p-3 text-center text-gray-300 
                                      ${isCurrent ? 'bg-orange-50 border-x-2 border-orange-400' : isPast ? 'bg-gray-100/50' : ''}
                                    `}
                                  >
                                    -
                                  </td>
                                );
                              }
                              
                              return (
                                <td 
                                  key={hour} 
                                  className={`p-3 text-center 
                                    ${isCurrent ? 'bg-orange-50 border-x-2 border-orange-400' : isPast ? 'bg-gray-100/50' : ''}
                                  `}
                                >
                                  <button
                                    onClick={() => status !== 'done' && canExecuteMedication && handleExecuteClick(animal, med, hour, status)}
                                    disabled={status === 'done' || !canExecuteMedication}
                                    title={!canExecuteMedication ? 'Usuário sem permissão para registrar medicação' : undefined}
                                    className={`
                                      w-full p-2 rounded-lg border-2 transition-all min-h-[50px]
                                      ${status === 'done' 
                                        ? 'bg-blue-100 border-blue-300 cursor-default' 
                                        : !canExecuteMedication
                                          ? 'bg-gray-100 border-gray-300 cursor-not-allowed opacity-70'
                                        : status === 'overdue'
                                          ? 'bg-red-100 border-red-300 hover:bg-red-200 cursor-pointer'
                                          : status === 'upcoming'
                                            ? 'bg-yellow-100 border-yellow-300 hover:bg-yellow-200 cursor-pointer animate-pulse'
                                            : 'bg-gray-100 border-gray-300 hover:bg-gray-200 cursor-pointer'
                                      }
                                    `}
                                    data-testid={`execute-btn-${med.protocol_id}-${hour}`}
                                  >
                                    <div className="flex flex-col items-center gap-1">
                                      {getStatusIcon(status)}
                                      <span className="text-xs font-medium">{getStatusLabel(status)}</span>
                                    </div>
                                  </button>
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              ))}
            </div>

            {/* Mobile: Card View */}
            <div className="lg:hidden space-y-4">
              {groupedSchedules.map((animal) => (
                <Card key={animal.animal_id} className="border-emerald-100" data-testid={`mobile-animal-card-${animal.animal_id}`}>
                  <CardHeader className="pb-2 bg-emerald-50">
                    <div className="flex items-start justify-between">
                      <div>
                        <Link 
                          to={`/animals/${animal.animal_id}/record`}
                          className="font-bold text-emerald-800 hover:underline flex items-center gap-1"
                        >
                          {animal.ibama_id}
                          <ExternalLink className="w-4 h-4" />
                        </Link>
                        {animal.pmp_id && <p className="text-xs text-sky-700">PMP: {animal.pmp_id}</p>}
                        <p className="text-sm text-gray-600">{animal.common_name}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge className={getGroupBadgeColor(animal.animal_group)}>
                            {animal.animal_group}
                          </Badge>
                          <span className="text-xs text-gray-500 flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> {animal.location} - {animal.bay}
                          </span>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3 pt-3">
                    {animal.medications.map((med) => (
                      <div key={med.protocol_id} className="border rounded-lg p-3 bg-gray-50">
                        <div className="flex items-center gap-2 mb-2">
                          <Pill className="w-4 h-4 text-emerald-600" />
                          <span className="font-semibold text-emerald-800">{isTherapyProtocol(med) ? (med.therapy_type || med.medication_name) : med.medication_name}</span>
                          {isTherapyProtocol(med) && <Badge className="bg-blue-100 text-blue-800">Terapia</Badge>}
                          {getMedicationProtocolStatusLabel(med) && <Badge className="bg-amber-100 text-amber-800">{getMedicationProtocolStatusLabel(med)}</Badge>}
                        </div>
                        <div className="flex flex-wrap items-center gap-2 mb-2 text-sm">
                          {!isTherapyProtocol(med) && med.dosage && <span className="text-gray-600">{med.dosage}</span>}
                          {!isTherapyProtocol(med) && med.route && (
                            <Badge className={getRouteBadgeColor(med.route)} variant="outline">
                              {med.route}
                            </Badge>
                          )}
                          {isTherapyProtocol(med) && med.therapy_duration && <span className="text-gray-600">{med.therapy_duration}</span>}
                          <span className="text-gray-400">{med.frequency}</span>
                          {med.start_time && (
                            <span className="text-emerald-600 text-xs">início {med.start_time}</span>
                          )}
                        </div>
                        {med.days_remaining !== null && (
                          <div className="flex items-center gap-1 text-xs mb-2">
                            <Calendar className="w-3 h-3" />
                            <span className={med.days_remaining <= 2 ? 'text-orange-600 font-medium' : 'text-gray-500'}>
                              {med.days_remaining} dia{med.days_remaining !== 1 ? 's' : ''} restante{med.days_remaining !== 1 ? 's' : ''}
                            </span>
                          </div>
                        )}
                        
                        {/* Horizontal scroll for time slots */}
                        <div className="flex gap-2 overflow-x-auto pb-2">
                          {med.scheduled_hours.map(hour => {
                            const status = med.hour_statuses?.[hour];
                            const isCurrent = isCurrentHourSlot(hour);
                            const isPast = isCurrentOrPastHour(hour) && !isCurrent;
                            return (
                              <button
                                key={hour}
                                onClick={() => status !== 'done' && canExecuteMedication && handleExecuteClick(animal, med, hour, status)}
                                disabled={status === 'done' || !canExecuteMedication}
                                title={!canExecuteMedication ? 'Usuário sem permissão para registrar medicação' : undefined}
                                className={`
                                  flex-shrink-0 p-3 rounded-lg border-2 min-w-[80px] transition-all relative
                                  ${isCurrent 
                                    ? 'ring-2 ring-orange-500 ring-offset-2' 
                                    : ''
                                  }
                                  ${status === 'done' 
                                    ? 'bg-blue-100 border-blue-300' 
                                    : !canExecuteMedication
                                      ? 'bg-gray-100 border-gray-300 opacity-70'
                                    : status === 'overdue'
                                      ? 'bg-red-100 border-red-300 animate-pulse'
                                      : status === 'upcoming'
                                        ? 'bg-yellow-100 border-yellow-300 animate-pulse'
                                        : isPast
                                          ? 'bg-gray-100 border-gray-300'
                                          : 'bg-white border-gray-200'
                                  }
                                `}
                                data-testid={`mobile-execute-btn-${med.protocol_id}-${hour}`}
                              >
                                {isCurrent && (
                                  <div className="absolute -top-2 left-1/2 transform -translate-x-1/2">
                                    <span className="bg-orange-500 text-white text-[10px] px-1.5 py-0.5 rounded-full font-bold">
                                      AGORA
                                    </span>
                                  </div>
                                )}
                                <div className="flex flex-col items-center gap-1">
                                  <span className={`font-bold text-sm ${isCurrent ? 'text-orange-600' : ''}`}>
                                    {formatHour(hour)}
                                  </span>
                                  {getStatusIcon(status)}
                                  <span className="text-xs">{getStatusLabel(status)}</span>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              ))}
            </div>
          </>
        )}
      </main>

      {/* Execute Medication Dialog */}
      <Dialog
        open={showExecuteDialog}
        onOpenChange={(open) => {
          setShowExecuteDialog(open);
          if (!open) setExecuteMediaFile(null);
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-800">
              <Syringe className="w-5 h-5" />
              {executingItem && isTherapyProtocol(executingItem.medication) ? 'Registrar Terapia' : 'Registrar Medicação'}
            </DialogTitle>
            <DialogDescription>
              {executingItem && isTherapyProtocol(executingItem.medication) ? 'Confirme a realização da terapia' : 'Confirme a aplicação do medicamento'}
            </DialogDescription>
          </DialogHeader>
          
          {executingItem && (
            <div className="space-y-4">
              <div className="p-3 bg-emerald-50 rounded-lg">
                <p className="font-medium text-emerald-800">{executingItem.ibama_id}</p>
                {executingItem.pmp_id && <p className="text-xs text-sky-700">PMP: {executingItem.pmp_id}</p>}
                <p className="text-sm text-gray-600">{executingItem.common_name}</p>
              </div>
              
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <Pill className="w-4 h-4 text-emerald-600" />
                  <span className="font-medium">
                    {isTherapyProtocol(executingItem.medication)
                      ? (executingItem.medication.therapy_type || executingItem.medication.medication_name)
                      : executingItem.medication.medication_name}
                  </span>
                </div>
                <p className="text-sm text-gray-600">
                  {isTherapyProtocol(executingItem.medication)
                    ? [executingItem.medication.therapy_duration, executingItem.medication.frequency].filter(Boolean).join(' - ')
                    : [executingItem.medication.dosage, executingItem.medication.route].filter(Boolean).join(' - ')}
                </p>
                <p className="text-sm text-emerald-600 mt-1">
                  Horário: {formatHour(executingItem.scheduled_hour)}
                </p>
                {executingItem.slot_status === 'overdue' && (
                  <p className="mt-1 text-xs font-medium text-red-600">
                    Protocolo atrasado. Informe quando a execução foi realizada.
                  </p>
                )}
              </div>

              {canSetProtocolExecutionDateTime(user) && (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <Label>Data executada</Label>
                    <Input
                      type="date"
                      value={executeDate}
                      onChange={(e) => setExecuteDate(e.target.value)}
                      data-testid="execute-date-input"
                    />
                  </div>
                  <div>
                    <Label>Hora executada</Label>
                    <Input
                      type="time"
                      value={executeTime}
                      onChange={(e) => setExecuteTime(e.target.value)}
                      data-testid="execute-time-input"
                    />
                  </div>
                </div>
              )}
              {canSetProtocolExecutionDateTime(user) && (
                <div>
                  <Label>Justificativa para data/hora retroativa</Label>
                  <Input
                    value={executeDateTimeJustification}
                    onChange={(e) => setExecuteDateTimeJustification(e.target.value)}
                    placeholder="Obrigatória quando a data/hora diferir do horário programado"
                    data-testid="execute-date-time-justification-input"
                  />
                </div>
              )}
              
              <div>
                <Label>Status</Label>
                <Select value={executeStatus} onValueChange={setExecuteStatus}>
                  <SelectTrigger data-testid="execute-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="realizado">Realizado</SelectItem>
                    <SelectItem value="nao_realizado">Não realizado</SelectItem>
                    <SelectItem value="atrasado">Atrasado (mas aplicado)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Observações (opcional)</Label>
                <Input
                  value={executeNotes}
                  onChange={(e) => setExecuteNotes(e.target.value)}
                  placeholder="Ex: Animal não colaborou, dose ajustada..."
                  data-testid="execute-notes-input"
                />
              </div>

              <div>
                <Label>Foto da execução (opcional)</Label>
                <CameraFileInput
                  accept="image/*"
                  disabled={submitting}
                  onFileSelected={setExecuteMediaFile}
                  fileLabel="Selecionar foto"
                  cameraLabel="Tirar foto"
                  data-testid="execute-photo-input"
                />
                {executeMediaFile && (
                  <p className="mt-1 text-xs text-gray-500">Selecionado: {executeMediaFile.name}</p>
                )}
              </div>
              
              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowExecuteDialog(false);
                    setExecuteMediaFile(null);
                  }}
                  className="flex-1"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleExecuteSubmit}
                  disabled={submitting}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  data-testid="confirm-execute-btn"
                >
                  {submitting ? 'Registrando...' : 'Confirmar'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-800">
              <Settings className="w-5 h-5" />
              Configurações
            </DialogTitle>
            <DialogDescription>
              Ajuste as preferências do dashboard
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Janela de alerta "próximo do horário"</Label>
              <div className="flex items-center gap-2 mt-2">
                <Input
                  type="number"
                  min={5}
                  max={120}
                  value={alertWindow}
                  onChange={(e) => setAlertWindow(parseInt(e.target.value) || 30)}
                  className="w-24"
                  data-testid="alert-window-input"
                />
                <span className="text-sm text-gray-600">minutos</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Medicações serão destacadas quando estiverem dentro desta janela de tempo
              </p>
            </div>
            
            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowSettings(false)}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleUpdateSettings}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                data-testid="save-settings-btn"
              >
                Salvar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
